import { c as _c } from "react/compiler-runtime";
import { ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Combobox as HuiCombobox } from '@headlessui/react';
import type { IconProps } from '@phosphor-icons/react';
import { CaretDown } from '@phosphor-icons/react';
import type { ButtonHTMLAttributes, ComponentType } from 'react';
import { forwardRef } from 'react';
import { List, ListItem } from '../list/list';
import type { TextFieldProps } from '../text-field/text-field';
import { TextField } from '../text-field/text-field';
type Base<T> = {
  options: T[];
  error?: string | undefined;
  required?: boolean;
  label?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  renderOption: (option: T) => React.ReactNode;
  getDisplayValue?: (option: T | null) => string;
  onInputChange?: (value: string) => void;
  getOptionDisabled?: (option: T) => boolean;
};
type Single<T> = Base<T> & {
  multiple?: false;
  value: T | null;
  onChange: (value: T | null) => void;
  isOptionSelected?: undefined;
};
type Multiple<T> = Base<T> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
  isOptionSelected?: (option: T, value: T[]) => boolean;
};
export type ComboboxProps<T> = (Multiple<T> | Single<T>) & {
  Icon?: ComponentType<IconProps>;
};
export function Combobox(t0) {
  const $ = _c(60);
  let Icon;
  let error;
  let getDisplayValue;
  let getOptionDisabled;
  let isLoading;
  let label;
  let multiple;
  let onChange;
  let onClose;
  let onInputChange;
  let options;
  let props;
  let renderOption;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    ({
      options,
      label,
      value,
      error,
      required: t1,
      multiple,
      Icon,
      disabled: t2,
      isLoading,
      onClose,
      onChange,
      getDisplayValue,
      renderOption,
      onInputChange,
      getOptionDisabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = error;
    $[3] = getDisplayValue;
    $[4] = getOptionDisabled;
    $[5] = isLoading;
    $[6] = label;
    $[7] = multiple;
    $[8] = onChange;
    $[9] = onClose;
    $[10] = onInputChange;
    $[11] = options;
    $[12] = props;
    $[13] = renderOption;
    $[14] = t1;
    $[15] = t2;
    $[16] = value;
  } else {
    Icon = $[1];
    error = $[2];
    getDisplayValue = $[3];
    getOptionDisabled = $[4];
    isLoading = $[5];
    label = $[6];
    multiple = $[7];
    onChange = $[8];
    onClose = $[9];
    onInputChange = $[10];
    options = $[11];
    props = $[12];
    renderOption = $[13];
    t1 = $[14];
    t2 = $[15];
    value = $[16];
  }
  const required = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const t3 = required ? `${label}*` : label;
  let t4;
  if ($[17] !== onInputChange) {
    t4 = event => onInputChange?.(event.target.value);
    $[17] = onInputChange;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const t5 = multiple ? value.length || 0 : undefined;
  const t6 = multiple && !!value.length;
  let t7;
  if ($[19] !== Icon || $[20] !== disabled || $[21] !== error || $[22] !== getDisplayValue || $[23] !== isLoading || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6) {
    t7 = <ComboboxInput as={AutoCompleteInput} Icon={Icon} error={error} isLoading={isLoading} label={t3} onChange={t4} displayValue={getDisplayValue} count={t5} shrinkLabel={t6} disabled={disabled} />;
    $[19] = Icon;
    $[20] = disabled;
    $[21] = error;
    $[22] = getDisplayValue;
    $[23] = isLoading;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== options.length) {
    t8 = options.length === 0 && <ComboboxOption disabled={true} value={null} as={ListItem} rounded={false} interactive={false} contentAs="div">Keine Ergebnisse gefunden</ComboboxOption>;
    $[29] = options.length;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== getOptionDisabled || $[32] !== multiple || $[33] !== options || $[34] !== props || $[35] !== renderOption || $[36] !== value) {
    let t10;
    if ($[38] !== getOptionDisabled || $[39] !== multiple || $[40] !== props || $[41] !== renderOption || $[42] !== value) {
      t10 = option => {
        const isOptionDisabled = getOptionDisabled?.(option);
        return <ComboboxOption as={ListItem} rounded={false} renderIcon={multiple ? _temp : undefined} key={option.id} value={option} selected={(multiple === true && props.isOptionSelected?.(option, value)) ?? (Array.isArray(value) && value.includes(option))} disabled={isOptionDisabled} className="ui-active:bg-hover-light" contentAsProps={{
          type: "button"
        }}>{renderOption(option)}</ComboboxOption>;
      };
      $[38] = getOptionDisabled;
      $[39] = multiple;
      $[40] = props;
      $[41] = renderOption;
      $[42] = value;
      $[43] = t10;
    } else {
      t10 = $[43];
    }
    t9 = options.map(t10);
    $[31] = getOptionDisabled;
    $[32] = multiple;
    $[33] = options;
    $[34] = props;
    $[35] = renderOption;
    $[36] = value;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[44] !== t8 || $[45] !== t9) {
    t10 = <ComboboxOptions as={List} className="absolute z-20 mt-2 max-h-96 w-full overflow-hidden overflow-y-auto rounded-lg border border-primary/30 bg-white py-2">{t8}{t9}</ComboboxOptions>;
    $[44] = t8;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t7) {
    t11 = <div className="relative">{t7}{t10}</div>;
    $[47] = t10;
    $[48] = t7;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  const Content = t11;
  if (multiple === true) {
    let t12;
    if ($[50] !== Content || $[51] !== onChange || $[52] !== onClose || $[53] !== value) {
      t12 = <HuiCombobox value={value} onChange={onChange} onClose={onClose} multiple={true}>{Content}</HuiCombobox>;
      $[50] = Content;
      $[51] = onChange;
      $[52] = onClose;
      $[53] = value;
      $[54] = t12;
    } else {
      t12 = $[54];
    }
    return t12;
  } else {
    let t12;
    if ($[55] !== Content || $[56] !== onChange || $[57] !== onClose || $[58] !== value) {
      t12 = <HuiCombobox value={value} onChange={onChange} onClose={onClose}>{Content}</HuiCombobox>;
      $[55] = Content;
      $[56] = onChange;
      $[57] = onClose;
      $[58] = value;
      $[59] = t12;
    } else {
      t12 = $[59];
    }
    return t12;
  }
}
function _temp(selected) {
  return <input type="checkbox" checked={selected} readOnly={true} className="pointer-events-none" />;
}
type AutoCompleteInputProps = TextFieldProps & {
  isLoading?: boolean;
  count?: number;
  Icon?: ComponentType<IconProps>;
};
const AutoCompleteInput = forwardRef<HTMLInputElement, AutoCompleteInputProps>((t0, ref) => {
  const $ = _c(11);
  let Icon;
  let count;
  let props;
  if ($[0] !== t0) {
    ({
      count,
      Icon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = count;
    $[3] = props;
  } else {
    Icon = $[1];
    count = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== count) {
    t1 = !!count && <Counter count={count} />;
    $[4] = count;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== Icon || $[7] !== props || $[8] !== ref || $[9] !== t1) {
    t2 = <TextField autoComplete="off" ref={ref} IconStart={Icon} Action={DropDownButton} flag={t1} {...props} />;
    $[6] = Icon;
    $[7] = props;
    $[8] = ref;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
});
AutoCompleteInput.displayName = 'AutoCompleteInput';
const DropDownButton = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CaretDown className="size-full ui-open:rotate-180" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <ComboboxButton {...props}>{t0}</ComboboxButton>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const Counter = t0 => {
  const $ = _c(2);
  const {
    count
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = <div className="grid h-5 min-h-5 place-content-center rounded-sm bg-primary px-2 py-0.5 font-base-caption text-white">{count}</div>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};